import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { signInUrl } from './return-url';

/**
 * Frontière entre ce qu'un visiteur peut faire et ce qui demande un compte.
 *
 * La règle de la plateforme : un visiteur consulte librement les producteurs,
 * les produits et les prix. Commander est possible sans compte (checkout invité)
 * ou avec un compte pour l'historique, les avis et les avantages VIP.
 *
 * Le contrôle est centralisé ici plutôt que répété dans chaque écran, pour deux
 * raisons : la règle reste vérifiable en un seul endroit, et l'URL de retour est
 * gérée uniformément, si bien qu'un visiteur qui s'inscrit au moment de
 * commander retrouve son produit et non la page d'accueil.
 *
 * Ce n'est qu'un garde-fou d'interface : il évite un aller-retour inutile et
 * clarifie le parcours. L'autorisation qui compte est celle de l'API, où le
 * jeton est vérifié — un contrôle côté navigateur se contourne toujours.
 */
@Injectable({ providedIn: 'root' })
export class AccountGate {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  /** Vrai lorsque l'utilisateur dispose d'un compte et d'une session valide. */
  readonly hasAccount = this.auth.isAuthenticated;

  /**
   * Vérifie qu'un compte est présent avant de poursuivre une action.
   *
   * Renvoie `true` si l'appelant peut continuer. Sinon, l'utilisateur est
   * conduit vers l'écran de connexion et l'appelant doit s'arrêter là.
   *
   * @param returnUrl Page à rejoindre après la connexion. Par défaut, la page
   *   courante : le visiteur reprend son parcours là où il l'avait laissé.
   */
  requireAccount(returnUrl?: string): boolean {
    if (this.auth.isAuthenticated()) {
      return true;
    }

    void this.router.navigateByUrl(signInUrl(returnUrl ?? this.router.url));
    return false;
  }
}
